import { Injectable } from '@angular/core';
import { AlertService } from '../../../core/services/alert.service';
import { Guid } from 'guid-typescript';
import { SolutionParticipationRequests } from 'src/app/core/services/solution-participation-requests.service';
import { SPProductFilterOptions, SPSummaryFilterOptions, solutionParticipationProductFilter, solutionParticipationSummaryFilter, SPViewFilterOptions, SortOptions, SPLocationTotalSolutionGroupsExpandedData, SpProductOrLocationExportPayload, AccountHierarchyResponse } from '../types/solution-participation-types';
import { BehaviorSubject, Observable } from 'rxjs';
import * as _ from 'lodash';
import { PaginationOptions } from 'src/app/features/solution-participation-details/types/solution-participation-types';
@Injectable({
  providedIn: 'root'
})
export class SolutionParticipationService {

  public defaultFiltersForSummaryDetail : { [id:string]: string[] }= {
    "brandList": [],
    "productList": [],
    "productGroup":[],
  };

  //"Solution Group View" tab
  public summaryAdvancedFilters = new BehaviorSubject<SPSummaryFilterOptions>({
    "platform": [],
    "solution": [],
    "productGroup": [],
    "productName": [],
    "productSku": [],
    "brand": []
  });

  //Solution Group View -> View by Product Details
  private productDetailsAdvancedFilters = new BehaviorSubject<SPProductFilterOptions>({
      "accountNumber": [],
      "accountName": [],
      "country": [],
      "state": [],
      "city": [],
      "zipCode": [],
      "productSku": [],
      "brand": []
  });

  //Solution Group View -> View by Location Details
  private locationDetailsAdvancedFilters = new BehaviorSubject<SPViewFilterOptions>({
    'accountNumber': [],
    'accountName': [],
    'country': [],
    'state': [],
    'city': [],
    'zipCode': [],
    'brand': [],
    'productGroup':[],
    'productName':[],
    'productSku': [],
  });

  //SP v3 ("Location View" tab)
  private locationViewAdvancedFilters = new BehaviorSubject<SPViewFilterOptions>({
    'accountNumber': [],
    'accountName': [],
    'country': [],
    'state': [],
    'city': [],
    'zipCode': [],
    'brand': [],
    'productGroup':[],
    'productName':[],
    'productSku': [],
  });

  SpProductorLocationExportPayload:SpProductOrLocationExportPayload ={
        exportType:'',
        contractModel:{
          division:'',
          dataView:'',
          regions:[],
          countries:[],
          period:'',
          presentationType:'',
          translation:'',
          topLevelContractGroup:[],
          secondLevelContractFilter:[],
          thirdLevelContractFilter:[],
          fourthLevelContractFilter:[],
          accts:[],
          dateRange: ''
        },
        queryFilters: [],
      };

  getSpProductorLocationExportPayload(): SpProductOrLocationExportPayload {
    return this.SpProductorLocationExportPayload;
  }

  getSpProductSKus(): string[] {
    const productList = this.defaultFiltersForSummaryDetail["productList"] ?? [];
    return productList;
  }

  //Track which radio button is selected
  private selectedRadioButtonSubject = new BehaviorSubject<string>('all');
  selectedRadioButtonObservable$ = this.selectedRadioButtonSubject.asObservable();

  //For the 'All' radio button, track which widget data to show ('rebate', 'mpl', or 'spl') based on available data
  private widgetAllTypeSubject = new BehaviorSubject<string>('all');
  widgetAllTypeObservable$ = this.widgetAllTypeSubject.asObservable();

  //Save rebate, mpl, spl, and current SPGroup data into observables so api calls do not need to be made every time.
  private rebateWidgetDataSubject = new BehaviorSubject<any>(undefined);
  rebateWidgetDataObservable$ = this.rebateWidgetDataSubject.asObservable();

  private mplWidgetDataSubject = new BehaviorSubject<any>(undefined);
  mplWidgetDataObservable$ = this.mplWidgetDataSubject.asObservable();

  private splWidgetDataSubject = new BehaviorSubject<any>(undefined);
  splWidgetDataObservable$ = this.splWidgetDataSubject.asObservable();

  private allSolutionGroupsDataSubject = new BehaviorSubject<any>({});
  allSolutionGroupsDataObservable$ = this.allSolutionGroupsDataSubject.asObservable();

  //track when first load of SP Groups table is completed
  private isSPGroupTableFirstLoadCompleteSubject = new BehaviorSubject<boolean>(false);
  SPGroupTableFirstLoadCompleteObservable$ = this.isSPGroupTableFirstLoadCompleteSubject.asObservable();

  // Track when the SP Groups table data is being (re)loaded - e.g. on widget type switch or currency change -
  // so dependent widgets (like the overview progress bar) can show a loading state instead of stale messages
  // computed from data that hasn't caught up to the current widget type selection yet.
  private isTableDataLoadingSubject = new BehaviorSubject<boolean>(false);
  tableDataLoadingObservable$ = this.isTableDataLoadingSubject.asObservable();

  private siteSolutionGroupDataSubject = new BehaviorSubject<SPLocationTotalSolutionGroupsExpandedData>(undefined as any);
  siteSolutionGroupDataObservable$ = this.siteSolutionGroupDataSubject.asObservable();

  private accountHierarchyValidationDataSubject = new BehaviorSubject<AccountHierarchyResponse | undefined>(undefined);
  public accountHierarchyValidationData$ = this.accountHierarchyValidationDataSubject.asObservable();

  private multipleRebatesExistForContractSubject = new BehaviorSubject<boolean>(false);
  public multipleRebatesExistForContract$ = this.multipleRebatesExistForContractSubject.asObservable();

  constructor(
    private _solPartService : SolutionParticipationRequests,
    private _alertService : AlertService
    ) { }

    private monthMap : {[index:string]:string} = {
      '1': 'Jan',
      '2': 'Feb',
      '3': 'Mar',
      '4': 'Apr',
      '5': 'May',
      '6': 'Jun',
      '7': 'Jul',
      '8': 'Aug',
      '9': 'Sep',
      '10': 'Oct',
      '11': 'Nov',
      '12': 'Dec'
    };

  public sumProducts: number = 0;
  public sumLocations: number = 0;
  public sumSales: number = 0;
  public sumCases: number = 0;
  public solutionsPerLocation: number = 0;
  public numSolutions: number = 0;

  setSummaryAdvancedFilters(advFilters:SPSummaryFilterOptions) {
    this.summaryAdvancedFilters.next(advFilters);
  }

  setProductDetailsAdvancedFilters(advFilters:SPProductFilterOptions) {
    this.productDetailsAdvancedFilters.next(advFilters);
  }

  getSummaryAdvancedFilters(): Observable<SPSummaryFilterOptions> {
    return this.summaryAdvancedFilters.asObservable();
  }

  getProductDetailsAdvancedFilters(): Observable<SPProductFilterOptions> {
    return this.productDetailsAdvancedFilters.asObservable();
  }

  setLocationDetailsAdvancedFilters(advFilters:SPViewFilterOptions) {
    this.locationDetailsAdvancedFilters.next(advFilters);
  }

  getLocationDetailsAdvancedFilters(): Observable<SPViewFilterOptions> {
    return this.locationDetailsAdvancedFilters.asObservable();
  }

  setLocationViewAdvancedFilters(advFilters:SPViewFilterOptions) {
    this.locationViewAdvancedFilters.next(advFilters);
  }

  getLocationViewAdvancedFilters(): Observable<SPViewFilterOptions> {
    return this.locationViewAdvancedFilters.asObservable();
  }

  // Add solution groups location pagination state
  private currentSGLSortingState = new BehaviorSubject<any>({
    sortDirections : {},
    sortBy : '',
    sortDirection : ''
  })
  public sglSortingState$ = this.currentSGLSortingState.asObservable();

  setSelectedRadioButton(widgetType: string) {
    this.selectedRadioButtonSubject.next(widgetType);
  }

  getSelectedRadioButton(): string {
    return this.selectedRadioButtonSubject.value;
  }

  setRebateWidgetData(data: any) {
    this.rebateWidgetDataSubject.next(data);
  }

  getRebateWidgetData(): any {
    return this.rebateWidgetDataSubject.value;
  }

  setMPLWidgetData(data: any) {
    this.mplWidgetDataSubject.next(data);
  }

  getMPLWidgetData(): any {
    return this.mplWidgetDataSubject.value;
  }

  setSPLWidgetData(data: any) {
    this.splWidgetDataSubject.next(data);
  }

  getSPLWidgetData(): any {
    return this.splWidgetDataSubject.value;
  }

  setAllSPGroupData(data: any) {
    this.allSolutionGroupsDataSubject.next(data);
  }

  setAccountHierarchyData(data:AccountHierarchyResponse | undefined){
    this.accountHierarchyValidationDataSubject.next(data);
  }

  getAccountHierarchyData(): AccountHierarchyResponse | undefined {
    return this.accountHierarchyValidationDataSubject.value;
  }

  setMultipleRebatesExistForContract(data: boolean) {
    this.multipleRebatesExistForContractSubject.next(data);
  }

  getMultipleRebatesExistForContract(): boolean {
    return this.multipleRebatesExistForContractSubject.value;
  }

  getAllSPGroupData(): any {
    return this.allSolutionGroupsDataSubject.value;
  }

  setSPGroupFirstLoadComplete(value: boolean) {
    this.isSPGroupTableFirstLoadCompleteSubject.next(value);
  }

  getSPGroupFirstLoadComplete(){
    return this.isSPGroupTableFirstLoadCompleteSubject.value;
  }

  // Added to fix a bug where switching widget tabs (rebate/mpl/spl/all) before the previous switch's
  // table data finished loading could show a stale/incorrect message ("No Agreements", "No Product
  // Lists entered", "No Data Available") on the overview progress bar (location-overview-gauge-chart),
  // since that widget computed its message from data/selection state that hadn't settled yet.
  // Call setTableDataLoading(true) whenever SolutionGroupsTableComponent sets its own isLoading = true,
  // and setTableDataLoading(false) once the corresponding data load/response has been applied
  // (currently centralized in initializeData()). Dependent widgets should treat this as "don't trust
  // selection/data state yet - show a loading state instead."
  setTableDataLoading(value: boolean) {
    this.isTableDataLoadingSubject.next(value);
  }

  getTableDataLoading(): boolean {
    return this.isTableDataLoadingSubject.value;
  }

  setTotalSolutionGroupsForSiteData(value: SPLocationTotalSolutionGroupsExpandedData) {
    this.siteSolutionGroupDataSubject.next(value);
  }

  getTotalSolutionGroupsForSiteData(){
    return this.siteSolutionGroupDataSubject.value;
  }

  resetWidgetData() {
    this.selectedRadioButtonSubject.next('all');
    this.widgetAllTypeSubject.next('all');
    this.rebateWidgetDataSubject.next(undefined);
    this.mplWidgetDataSubject.next(undefined);
    this.splWidgetDataSubject.next(undefined);
    this.allSolutionGroupsDataSubject.next({});
    this.currentSGLSortingState.next({
      sortDirections : {},
      sortBy : '',
      sortDirection : ''
    });
    this.setSPGroupFirstLoadComplete(false);

    this.updatePaginationState({
      limit: 25,
      page: 1,
      sortBy: "",
      sortOrder: ""
    })

    this.siteSolutionGroupDataSubject.next(undefined as any);
  }

  updateDefaultFiltersForProduct(
    data: solutionParticipationSummaryFilter[],
    solutionGroupName: string
  ) {
    let summFilters = this.summaryAdvancedFilters.getValue();
    // By Solution
    let filteredData: solutionParticipationSummaryFilter[] = data.filter(
      (d: solutionParticipationSummaryFilter) =>
        solutionGroupName == d['solution']
    );
    // By Product Group
    if (summFilters['productGroup'] && summFilters['productGroup'].length > 0) {
      filteredData = filteredData.filter(
        (d: solutionParticipationSummaryFilter) =>
          summFilters['productGroup'].includes(d['productGroup'])
      );
    }

    // By Brand
    if (summFilters['brand'] && summFilters['brand'].length > 0) {
      filteredData = filteredData.filter((d: any) =>
        summFilters['brand'].includes(d['brand'])
      );
    }

    // By Product Name
    if (summFilters['productName'] && summFilters['productName'].length > 0) {
      filteredData = filteredData.filter(
        (d: solutionParticipationSummaryFilter) =>
          summFilters['productName'].includes(d['productName'])
      );
    }
    // Get distinct product SKUs from the advanced filters that were set.
    if (summFilters['productSku'] && summFilters['productSku'].length > 0) {
      filteredData = filteredData.filter(
        (d: solutionParticipationSummaryFilter) =>
          summFilters['productSku'].includes(d['productSku'])
      );
    }

    // Get distinct Product Skus for a given Solution Group
    // This is used to help filter the location/product details in the api
    const distinctSKUs: string[] = Array.from(
      new Set(
        filteredData.map(
          (d: solutionParticipationSummaryFilter) => d.productSku
        )
      )
    );
    // Set the default Filters
    // For productList, if there are productSkus in the summary advanced filters, use those as the default filters, otherwise use all the distinct SKUs for that solution group.
    this.defaultFiltersForSummaryDetail['productList'] = summFilters['productSku'].length > 0 ? _.cloneDeep(summFilters['productSku']) : [...distinctSKUs];
    this.defaultFiltersForSummaryDetail["brandList"] = _.cloneDeep(summFilters['brand']);
    this.defaultFiltersForSummaryDetail["productGroup"] = _.cloneDeep(summFilters['productGroup']);
    this.defaultFiltersForSummaryDetail["produtName"] = _.cloneDeep(summFilters['productName']);
  }

  async getSummary(advFilters: { [id: string]: string[] }) {
    return this._solPartService.getSolutionParticipationSummary(advFilters).then(response => {
      const { data } = response;
        return data;

      }).catch(error => {
        this._handleError('Retrieving Solution Participation Summary data failed.', error);
      });
  }

  async getSummaryFilters(): Promise<solutionParticipationSummaryFilter[]> {
    return this._solPartService.getSolutionParticipationSummaryFilters().then(response => {
      const { data } = response;
        return data as solutionParticipationSummaryFilter[];
      }).catch(error => {
        this._handleError('Retrieving Solution Summary data failed.', error);
        return [];
      });
  }

  async getSummaryDetailsAdvFilters(): Promise<solutionParticipationProductFilter[]> {
    return this._solPartService.getSolutionSummaryDetailsAdvFilters().then(response => {
      const { data } = response;
        return data as solutionParticipationProductFilter[];
      }).catch(error => {
        this._handleError('Retrieving Solution Participation Product Detail data failed.', error);
        return [];
      });
  }

  async getCases(advFilters: { [id: string]: string[] }) {
    return this._solPartService.getSolutionParticipationCases(advFilters).then(response => {
      const { data } = response;
        return data;
      }).catch(error => {
        this._handleError('Retrieving Solution Participation Cases data failed.', error);
      });
  }

  async getSales(advFilters: { [id: string]: string[] }) {
    return this._solPartService.getSolutionParticipationSales(advFilters).then(response => {
      const { data } = response;
        return data;
      }).catch(error => {
        this._handleError('Retrieving Solution Participation Sales data failed.', error);
      });
  }

  //Solution Participation v3 (solution groups by location - US:473787/8)
  async getSolutionGroupsByLocation(widgetType: string, paginationOptions: PaginationOptions, advProductFilters: SPProductFilterOptions,useCache: boolean = true) {
    let advFilters = advProductFilters;
    if (this.isAdvancedFiltersEmpty(Object.values(advFilters).filter((v): v is string[] => Array.isArray(v)))) {
      // Deep clone so as to call by value.
      advFilters = await this.addDefaultFilters(_.cloneDeep(advProductFilters));
    }
    return await this._solPartService.getSolutionGroupsByLocation(widgetType, paginationOptions, advFilters,useCache).then(response => {
      const { data } = response;
      if (data != undefined) {
        return data;
      }
    }).catch(error => {
      this._handleError('Retrieving Solution Group Locations data failed.', error);
    });
  }

  async getByLocationData(solution: string | undefined, advProductFilters: SPProductFilterOptions,
    paginationOptions: PaginationOptions,locationId?:string,isLocationDrillIn:boolean=false
  ) {

    let advFilters = advProductFilters;
    // Deep clone so as to call by value.
    if (this.isAdvancedFiltersEmpty(Object.values(advFilters)) && !isLocationDrillIn) {
      advFilters = await this.addDefaultFilters(_.cloneDeep(advProductFilters));
    }

    return this._solPartService.getGroupByLocation(solution, advFilters,paginationOptions,locationId,isLocationDrillIn).then(response => {
      const { data } = response;
        return data;
      }).catch(error => {
        this._handleError('Retrieving group by location - Solution Participation  data failed.', error);
      });
  }

  async getByProductData(solution: string | undefined, advProductFilters: SPProductFilterOptions,
    paginationOptions: PaginationOptions,productCode?:string,isProductDrillIn:boolean=false
  ) {

    let advFilters = advProductFilters;
    if (this.isAdvancedFiltersEmpty(Object.values(advFilters)) && !isProductDrillIn) {
      // Deep clone so as to call by value.

      advFilters = await this.addDefaultFilters(_.cloneDeep(advProductFilters));
    }

    return this._solPartService.getGroupByProducts(solution, advFilters,paginationOptions,isProductDrillIn,productCode).then(response => {
      const { data } = response;
        return data;
      }).catch(error => {
        this._handleError('Retrieving group by product - Solution Participation  data failed.', error);
      });
  }

  async addDefaultFilters(advProductFilters: SPProductFilterOptions) {
    let tempFilters = {...advProductFilters}
    // Add the Skus
    if (this.defaultFiltersForSummaryDetail["productList"].length > 0) {
      let values:string[] = this.defaultFiltersForSummaryDetail["productList"];
      for (const value of values) {
        if (!tempFilters["productSku"].includes(value)) {
          tempFilters["productSku"].push(value);
        }
      }
    }
    // Add the brands
    if (this.defaultFiltersForSummaryDetail["brandList"].length > 0) {
      let values:string[] = this.defaultFiltersForSummaryDetail["brandList"];
      for (const value of values) {
        if (!tempFilters["brand"].includes(value)) {
          tempFilters["brand"].push(value);
        }
      }
    }

    // Add the PRODUCT GROUP
    if (this.defaultFiltersForSummaryDetail["productGroup"]?.length > 0) {
      const values: string[] = this.defaultFiltersForSummaryDetail["productGroup"] ?? [];

      // Ensure tempFilters["productGroup"] is defined as an array
      tempFilters["productGroup"] = tempFilters["productGroup"] ?? [];


      for (const value of values) {
        if (!tempFilters["productGroup"].includes(value)) {
          tempFilters["productGroup"].push(value);
        }
      }
    }

    // Add the PRODUCT NAME
    if (this.defaultFiltersForSummaryDetail["productName"]?.length > 0) {
      const values: string[] = this.defaultFiltersForSummaryDetail["productName"] ?? [];

      // Ensure tempFilters["productName"] is defined as an array
      tempFilters["productName"] = tempFilters["productName"] ?? [];


      for (const value of values) {
        if (!tempFilters["productName"].includes(value)) {
          tempFilters["productName"].push(value);
        }
      }
    }

    return tempFilters;
  }

  async getSPRebateWidgetData(widgetType: string, advProductFilters: SPProductFilterOptions) {
    let advFilters = advProductFilters;
    if (this.isAdvancedFiltersEmpty(Object.values(advFilters))) {
      // Deep clone so as to call by value.
      advFilters = await this.addDefaultFilters(_.cloneDeep(advProductFilters));
    }
    return this._solPartService.getSPRebateWidgetData(widgetType, advFilters).then(response => {
      const { data } = response;
        return data;
      }).catch(error => {
        this._handleError('Retrieving SP Rebate Widget data failed.', error);
      });
  }

  async getSPProductWidgetData(widgetType: string, advProductFilters: SPProductFilterOptions) {
    let advFilters = advProductFilters;
    if (this.isAdvancedFiltersEmpty(Object.values(advFilters))) {
      // Deep clone so as to call by value.
      advFilters = await this.addDefaultFilters(_.cloneDeep(advProductFilters));
    }
    return this._solPartService.getSPProductWidgetData(widgetType, advFilters).then(response => {
      const { data } = response;
        return data;
      }).catch(error => {
        this._handleError('Retrieving SP Products Widget data failed.', error);
      });
  }

  async getSPTotalSolutionGroupsExpandedData(siteId: string, widgetType: string) {
    return this._solPartService.getSPTotalSolutionGroupsExpandedData(siteId, widgetType).then(response => {
      const { data } = response;
        return data;
      }).catch(error => {
        this._handleError('Retrieving SP Total Solution Groups Expanded data failed.', error);
      });
  }

  async exportSolutionGroupsData(widgetType: string, paginationOptions: PaginationOptions, advProductFilters?: { [id: string]: string[] }) {
    try {
      const data = await this._solPartService.exportSolutionGroupsData(widgetType,paginationOptions, advProductFilters);
      return data;
    } catch (error) {
      this._handleError('Downloading Solutions Group Excel file failed.', error);
      return null;
    }
  }

  async DownloadExcel(payload: SpProductOrLocationExportPayload) {
    try {
      const data = await this._solPartService.downloadExcel(payload);
      return data;
    } catch (error) {
      this._handleError('Downloading Excel file failed.', error);
      return null;
    }
  }

  async ValidateAccountHierarchy() {
    try{
    return this._solPartService.validateAccountHierarchy().then(response => {
      const { data } = response;
        return data;
      }).catch(error => {
        this._handleError('Validating Account Hierarchy failed.', error);
      });}catch(error){
        this._handleError('Validating Account Hierarchy failed.', error);
        return null;
    }
  }

  async hasDataWithMultipleRebateAgreements() : Promise<boolean> {
    try{
      return this._solPartService.checkForMultipleRebateAgreements().then(response => {
        const { data } = response;
          return data;
        }).catch(error => {
          this._handleError('Checking for multiple agreements failed.', error);
          return false;
      });
    } catch(error) {
      this._handleError('Checking for multiple agreements failed.', error);
      return false;
    }
  }

  //todo endpoint call for groupByLocation

  private _handleError(errorMessage: string, error: any) {
    console.log(errorMessage + ':' + JSON.stringify(error));
    this._alertService.addAlert({
      message: `${errorMessage} If problem persists, please report a bug.`,
      id: Guid.create()
    });
  }

  mapMonthNumberToMonthAbbreviation(monthNumber : string){
    const retval = this.monthMap[monthNumber];
    if (!retval) console.log('Unknown Month Number : ' + JSON.stringify(monthNumber));
    return retval;
  }

  resetAllAdvancedFilters() {
    this.resetSummaryAdvancedFilters();
    this.resetLocationAdvancedFilters();
    this.resetProductAdvancedFilters();
    this.resetLocationViewAdvancedFilters();
  }

  resetSummaryAdvancedFilters() {
    this.setSummaryAdvancedFilters({
      "platform": [],
      "solution": [],
      "productGroup": [],
      "productName": [],
      "productSku":[],
      "brand": []
    });
  }

  resetLocationAdvancedFilters() {
    this.setLocationDetailsAdvancedFilters({
      accountNumber: [],
      accountName: [],
      country: [],
      state: [],
      city: [],
      zipCode: [],
      brand: [],
      productGroup:[],
      productName:[],
      productSku: [],
    });
    this.resetSummaryDetailFilters();
  }

  resetProductAdvancedFilters() {
    this.resetSummaryDetailFilters();
    this.setProductDetailsAdvancedFilters({
      accountNumber: [],
      accountName: [],
      country: [],
      state: [],
      city: [],
      zipCode: [],
      productSku: [],
      brand: []
    });
  }

  resetSummaryDetailFilters(){
    this.defaultFiltersForSummaryDetail ={
      brandList: [],
      productList: [],
      productGroup: [],
      productName: []
    };
  }

  resetLocationViewAdvancedFilters() {
    this.setLocationViewAdvancedFilters({
      accountNumber: [],
      accountName: [],
      country: [],
      state: [],
      city: [],
      zipCode: [],
      brand: [],
      productGroup:[],
      productName:[],
      productSku: [],
    });
  }

  clearCache() {
    this._solPartService.clearCache();
    this.setAccountHierarchyData(undefined);
  }

  clearProductViewCache(){
    this._solPartService.clearProductViewCache();
  }

  clearLocationViewCache(){
    this._solPartService.clearLocationViewCache();
  }

  getPaginationState(): PaginationOptions {
    return this._solPartService.getPaginationState();
  }

  updatePaginationState(options: Partial<PaginationOptions>) {
    this._solPartService.updatePaginationState(options);
  }

  getSGLSortingState(): SortOptions {
    return this.currentSGLSortingState.value;
  }

  updateSGLSortingState(options: Partial<SortOptions>) {
    this.currentSGLSortingState.next({
      ...this.currentSGLSortingState.value,
      ...options
    });
  }

  private isAdvancedFiltersEmpty(values:string[][]){
    return values.every((v: string[]) => v.length === 0);
  }

}
